import * as React from "react"
import { CategoryForm } from "@/components/categories/category-form"
import { GroupCategoriesSection } from "@/components/categories/group-categories-section"
import { ResponsiveContainer } from "@/components/layout/responsive-grid"
import { LoadingState } from "@/components/ui/loading-state"
import { useAuthStore } from "@/stores/auth.store"

import { useUserGroups } from "@/hooks/queries/useGroups"
import { useCreateCategory, useUpdateCategory, useDeleteCategory } from "@/hooks/mutations/useCreateCategory"
import { useUpdateCategorySplits } from "@/hooks/mutations/useUpdateCategory"
import { useToggleCategoryPin } from "@/hooks/mutations/useToggleCategoryPin"
import { useCategorySplits } from "@/hooks/queries/useCategories"
import { toast } from "sonner"
import type { Category, Group } from "@/types/domain.types"
import { logger } from '@/lib/logger'


export const CategoriesPage: React.FC = () => {
  const { user: _user } = useAuthStore()
  const [showCategoryForm, setShowCategoryForm] = React.useState(false)
  const [editingCategory, setEditingCategory] = React.useState<Category | undefined>()
  const [selectedGroupId, setSelectedGroupId] = React.useState<string | null>(null)
  const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(new Set())

  // Fetch all user's groups
  const { data: groups = [], isLoading: isLoadingGroups, error: groupsError } = useUserGroups()

  // Fetch splits for the editing category
  const { data: categorySplits, isLoading: isLoadingSplits } = useCategorySplits(editingCategory?.id || '')

  // Mutations
  const createCategoryMutation = useCreateCategory()
  const updateCategoryMutation = useUpdateCategory()
  const updateCategorySplitsMutation = useUpdateCategorySplits()
  const deleteCategoryMutation = useDeleteCategory()
  const toggleCategoryPinMutation = useToggleCategoryPin()

  // Toggle group expansion
  const toggleGroupExpanded = (groupId: string) => {
    const newExpanded = new Set(expandedGroups)
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId)
    } else {
      newExpanded.add(groupId)
    }
    setExpandedGroups(newExpanded)
  }

  // Auto-expand groups on mount to show content
  React.useEffect(() => {
    if (groups.length > 0 && expandedGroups.size === 0) {
      // Auto-expand first group or all groups if there are only a few
      if (groups.length <= 3) {
        setExpandedGroups(new Set(groups.map(g => g.id)))
      } else {
        setExpandedGroups(new Set([groups[0].id]))
      }
    }
  }, [groups])

  const handleCreateCategory = (groupId: string) => {
    setSelectedGroupId(groupId)
    setEditingCategory(undefined)
    setShowCategoryForm(true)
  }

  const handleCategoryEdit = (category: Category, groupId: string) => {
    setSelectedGroupId(groupId)
    setEditingCategory(category)
    setShowCategoryForm(true)
  }

  const handleCategoryDelete = async (categoryId: string, _groupId: string) => {
    if (!confirm('Are you sure you want to delete this category?')) return

    try {
      await deleteCategoryMutation.mutateAsync(categoryId)
      toast.success('Category deleted successfully')
    } catch (error) {
      toast.error('Failed to delete category')
      logger.error('Failed to delete category:', { error })
    }
  }

  const handleCategoryTogglePin = async (categoryId: string, _isPinned: boolean, _groupId: string) => {
    try {
      await toggleCategoryPinMutation.mutateAsync({ categoryId })
    } catch (error) {
      logger.error('Failed to toggle pin:', { error })
    }
  }

  const handleSubmitCategory = async (data: {
    name: string;
    description?: string;
    emoji?: string;
    default_splits?: Array<{
      user_id: string;
      weight: number;
    }>;
  }) => {
    if (!selectedGroupId) return

    try {
      if (editingCategory) {
        // Update category basic info
        await updateCategoryMutation.mutateAsync({
          categoryId: editingCategory.id,
          ...data
        })

        // Update splits if they were provided
        if (data.default_splits && data.default_splits.length > 0) {
          await updateCategorySplitsMutation.mutateAsync({
            categoryId: editingCategory.id,
            splits: data.default_splits.map(split => ({
              user_id: split.user_id,
              weight: Number(split.weight) // Convert to number
            }))
          })
        }

        toast.success(`Category "${data.name}" updated successfully!`)
      } else {
        await createCategoryMutation.mutateAsync({
          groupId: selectedGroupId,
          ...data
        })
        toast.success(`Category "${data.name}" created successfully!`)
      }
      setShowCategoryForm(false)
      setEditingCategory(undefined)
      setSelectedGroupId(null)
    } catch (error) {
      logger.error('Failed to save category:', { error })
      toast.error(`Failed to ${editingCategory ? 'update' : 'create'} category`)
    }
  }

  const isLoading = isLoadingGroups

  if (isLoading) {
    return <LoadingState message="Loading groups and categories..." />
  }

  if (groupsError) {
    return (
      <ResponsiveContainer size="xl">
        <div className="text-center py-12">
          <h2 className="text-2xl font-semibold text-destructive mb-2">
            Failed to load groups
          </h2>
          <p className="text-muted-foreground">
            Please try refreshing the page
          </p>
        </div>
      </ResponsiveContainer>
    )
  }

  if (groups.length === 0) {
    return (
      <ResponsiveContainer size="xl">
        <div className="text-center py-12">
          <h2 className="text-2xl font-semibold text-muted-foreground mb-2">
            No groups yet
          </h2>
          <p className="text-muted-foreground">
            Create or join a group to start managing categories
          </p>
        </div>
      </ResponsiveContainer>
    )
  }

  // Get members for the selected group (for the form)
  const selectedGroup = groups.find(g => g.id === selectedGroupId) ?? groups.length > 0 ? groups[0] : undefined
  const groupMembers = selectedGroup?.users || []

  // Debug splits fetching - must be after all hooks
  // React.useEffect(() => {
  //   if (editingCategory && categorySplits) {
  //     logger.debug('Editing category:', editingCategory.id, editingCategory.name)
  //     logger.debug('Fetched splits:', categorySplits)
  //   }
  // }, [editingCategory, categorySplits])

  return (
    <ResponsiveContainer size="xl">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Categories</h1>
            <p className="text-muted-foreground">
              Organize expenses by category across all your groups
            </p>
          </div>
        </div>

        <div className="space-y-4">
          {groups.map((group) => (
            <GroupCategoriesSection
              key={group.id}
              group={group as Group}
              isExpanded={expandedGroups.has(group.id)}
              onToggleExpand={() => toggleGroupExpanded(group.id)}
              onCreateCategory={handleCreateCategory}
              onEditCategory={handleCategoryEdit}
              onDeleteCategory={handleCategoryDelete}
              onTogglePinCategory={handleCategoryTogglePin}
            />
          ))}
        </div>

        <CategoryForm
          open={showCategoryForm}
          onOpenChange={(open) => {
            setShowCategoryForm(open)
            if (!open) {
              setEditingCategory(undefined)
              setSelectedGroupId(null)
            }
          }}
          category={editingCategory ? {
            ...editingCategory,
            // Only add splits if they're loaded (or if we're not loading)
            default_splits: (!isLoadingSplits && categorySplits) ?
              categorySplits.map(split => ({
                user_id: split.user_id,
                weight: Number(split.weight) // Ensure weight is a number
              })) as any
              : editingCategory.default_splits // Use existing if still loading
          } : undefined}
          groupMembers={groupMembers}
          onSubmit={handleSubmitCategory}
        />
      </div>
    </ResponsiveContainer>
  )
}